import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import moment from "moment";
import Breadcrumbs from "components/Common/Breadcrumb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiGet, apiPut } from "helpers/apiService";
import createNew from "assets/img/new-email.png";

const getTemplateId = template =>
  template?.id || template?.projectMasterLandingPageTemplateId;

const MasterLandingPageList = props => {
  document.title = "Master Landing Pages | Realty Space";

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [selected, setSelected] = useState(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [renameDescription, setRenameDescription] = useState("");

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiGet(
        "ProjectMasterLandingPageTemplate/alltemplates"
      );
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.resultData)
          ? response.data.resultData
          : Array.isArray(response)
            ? response
            : [];
      setTemplates(data);
    } catch (error) {
      console.error("Error fetching master landing templates:", error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const openRename = template => {
    setSelected(template);
    setRenameTitle(template.templateTitle || "");
    setRenameDescription(template.templateDescription || "");
    setRenameOpen(true);
  };

  const closeRename = () => {
    if (renaming) return;
    setRenameOpen(false);
    setSelected(null);
    setRenameTitle("");
    setRenameDescription("");
  };

  const saveRename = async event => {
    event.preventDefault();
    const title = renameTitle.trim();
    const templateId = getTemplateId(selected);

    if (!templateId) {
      toast.error("Invalid template selected");
      return;
    }
    if (!title) {
      toast.error("Please enter a template name.");
      return;
    }

    try {
      setRenaming(true);
      toast.loading("Renaming template...");

      // Load full template so rename doesn't wipe saved design content.
      let full = selected;
      try {
        const detailResponse = await apiGet(
          "ProjectMasterLandingPageTemplate/templatebyid",
          { id: templateId }
        );
        const detail =
          detailResponse?.data?.resultData ||
          detailResponse?.data?.data ||
          detailResponse?.data ||
          detailResponse ||
          null;
        if (detail && !Array.isArray(detail)) {
          full = detail;
        } else if (Array.isArray(detail) && detail[0]) {
          full = detail[0];
        }
      } catch (error) {
        // Fall back to list-row data if detail call fails.
      }

      const payload = {
        id: Number(templateId),
        templateTitle: title,
        templateDescription: renameDescription.trim(),
        templateContent: full.templateContent || selected.templateContent || "",
        templateCSS: full.templateCSS || selected.templateCSS || "",
        templateType: full.templateType ?? selected.templateType ?? 1,
        publishStatusId:
          full.publishStatusId ?? selected.publishStatusId ?? 0,
        isActive: full.isActive ?? selected.isActive ?? true,
      };

      await apiPut("ProjectMasterLandingPageTemplate", payload);
      toast.dismiss();
      toast.success("Template renamed");
      setRenameOpen(false);
      setSelected(null);
      fetchTemplates();
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to rename template"
      );
    } finally {
      setRenaming(false);
    }
  };

  return (
    <div className="page-content project-page-content">
      <ToastContainer autoClose={2000} />
      {loading ? <div className="loading">Loading…</div> : null}
      <Container fluid>
        <Row className="project-header mb20">
          <Col sm="7">
            <Breadcrumbs
              title={props.t("Master")}
              breadcrumbItem={props.t("Master Landing Pages")}
            />
          </Col>
          <Col sm="5">
            <div className="mt-3 float-end">
              <Link
                to="/master-landing-page/create"
                className="btn btn-primary btn-md"
              >
                {props.t("Add Master Landing Page")}{" "}
                <i className="mdi mdi-plus-box-outline ms-1" />
              </Link>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg="3" className="mb-4">
            <Card className="h-100">
              <CardBody className="p-0 text-center">
                <Link
                  to="/master-landing-page/create"
                  className="previewClass card-link tempLink d-block p-3"
                >
                  <img
                    src={createNew}
                    className="img-fluid"
                    alt="Add master landing page"
                  />
                </Link>
              </CardBody>
              <CardFooter>
                <Link
                  to="/master-landing-page/create"
                  className="previewClass card-link tempLink"
                >
                  Add Master Landing Page
                </Link>
              </CardFooter>
            </Card>
          </Col>

          {templates.map(template => {
            const templateId = getTemplateId(template);
            return (
              <Col lg="3" className="mb-4" key={templateId}>
                <Card className="h-100">
                  <CardHeader>
                    <h5 className="mb-0">
                      <Link
                        to={`/master-landing-page/edit/${templateId}`}
                        className="text-dark text-decoration-none"
                      >
                        {template.templateTitle || "Untitled Template"}
                      </Link>
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {template.templateDescription ? (
                      <p className="mb-2 text-muted">
                        {template.templateDescription}
                      </p>
                    ) : null}
                    <p className="mb-1 text-muted">
                      Type:{" "}
                      {template.templateType === 1
                        ? "Landing Page"
                        : template.templateType || "N/A"}
                    </p>
                    <p className="mb-0 text-muted">
                      Created:{" "}
                      {template.createdDate
                        ? moment(template.createdDate).format("D MMM YY")
                        : "N/A"}
                    </p>
                    <p className="mb-0 text-muted">
                      Status:{" "}
                      {template.publishStatusId === 1 ? "Published" : "Draft"}
                    </p>
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      <Link
                        to={`/master-landing-page/edit/${templateId}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Edit Template
                      </Link>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        type="button"
                        onClick={() => openRename(template)}
                      >
                        Rename
                      </Button>
                    </div>
                  </CardBody>
                  <CardFooter className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">
                      Master Template #{templateId}
                    </span>
                    <Link
                      to={`/master-landing-page/edit/${templateId}`}
                      className="text-primary"
                    >
                      Edit
                    </Link>
                  </CardFooter>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>

      <Modal isOpen={renameOpen} toggle={closeRename} centered>
        <Form onSubmit={saveRename}>
          <ModalHeader toggle={closeRename}>Rename Template</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label htmlFor="renameTitle">
                Template Name <span className="text-danger">*</span>
              </Label>
              <Input
                id="renameTitle"
                type="text"
                value={renameTitle}
                onChange={e => setRenameTitle(e.target.value)}
                placeholder="Template name"
                required
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label htmlFor="renameDescription">Description</Label>
              <Input
                id="renameDescription"
                type="textarea"
                rows="3"
                value={renameDescription}
                onChange={e => setRenameDescription(e.target.value)}
                placeholder="Optional short description"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="secondary"
              onClick={closeRename}
              disabled={renaming}
            >
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={renaming}>
              {renaming ? "Saving..." : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

MasterLandingPageList.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(MasterLandingPageList);
